import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateEmbarque } from "@/hooks/useEmbarques";
import { useProveedores } from "@/hooks/useProveedores";
import { useToast } from "@/hooks/use-toast";
import { ClienteSelectField } from "@/components/ClienteSelectField";
import { ProveedorSelectField } from "@/components/ProveedorSelectField";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultProveedorId?: string;
  defaultClienteId?: string;
}

export function CreateEmbarqueModal({ open, onOpenChange, defaultProveedorId, defaultClienteId }: Props) {
  const [numeroDo, setNumeroDo] = useState("");
  const [proveedorId, setProveedorId] = useState(defaultProveedorId || "");
  const [clienteId, setClienteId] = useState(defaultClienteId || "");
  const [fechaLlegada, setFechaLlegada] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const createEmbarque = useCreateEmbarque();
  const { data: proveedores = [] } = useProveedores();
  const { toast } = useToast();

  useEffect(() => {
    if (defaultProveedorId) setProveedorId(defaultProveedorId);
  }, [defaultProveedorId]);

  useEffect(() => {
    if (defaultClienteId) setClienteId(defaultClienteId);
  }, [defaultClienteId]);

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    if (!numeroDo.trim()) {
      toast({ title: "Error", description: "El número DO es obligatorio.", variant: "destructive" });
      return;
    }
    if (!clienteId) {
      toast({
        title: "Cliente requerido",
        description: "Asigna el cliente importador para que el embarque sea visible en el Portal B2B.",
        variant: "destructive",
      });
      return;
    }
    try {
      const selectedProv = proveedores.find((p) => p.id === proveedorId);
      await createEmbarque.mutateAsync({
        numero_do: numeroDo.trim(),
        proveedor: selectedProv?.nombre || undefined,
        proveedor_id: proveedorId || undefined,
        cliente_id: clienteId,
        fecha_llegada_estimada: fechaLlegada || undefined,
      });
      toast({ title: "✅ Embarque creado", description: `DO: ${numeroDo}` });
      setNumeroDo("");
      setProveedorId("");
      setClienteId("");
      setFechaLlegada("");
      setAttemptedSubmit(false);
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleProvCreated = (prov: any) => {
    if (prov?.id) setProveedorId(prov.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Nuevo Embarque</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 min-w-0">

          <div className="space-y-2">
            <Label htmlFor="numero_do">Número DO *</Label>
            <Input id="numero_do" value={numeroDo} onChange={(e) => setNumeroDo(e.target.value)} placeholder="DO-2026-001" />
          </div>

          <div className="space-y-2 min-w-0">

            <Label>
              Cliente Importador <span className="text-destructive">*</span>
            </Label>
            <ClienteSelectField
              value={clienteId}
              onValueChange={setClienteId}
              required
              invalid={attemptedSubmit && !clienteId}
            />
            <p className="text-[11px] text-muted-foreground">
              Obligatorio: define quién verá este embarque en el Portal B2B.
            </p>
          </div>

          <div className="space-y-2 min-w-0">

            <Label>Proveedor</Label>
            <ProveedorSelectField
              value={proveedorId}
              onValueChange={setProveedorId}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fecha_llegada">Fecha Estimada de Llegada</Label>
            <Input id="fecha_llegada" type="date" value={fechaLlegada} onChange={(e) => setFechaLlegada(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={createEmbarque.isPending}>
            {createEmbarque.isPending ? "Creando..." : "Crear Embarque"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
